import type { JSX } from "preact";
import { useEffect, useState } from "preact/hooks";
import { Label } from "@gss/components/Label";
import type {
    FilterOption,
    Filter as StatFilter,
} from "@gss/types/measurementStat";
import type { Selected } from "../types";
import { MultiSelect } from "../util/MultiSelect";
import type { MultiSelectChild } from "../util/MultiSelect";
import { useTranslation } from "../util/Translation";
import { FilterButtons } from "./FilterButtons";
import {
    combineSelected,
    isAllOption,
    makeSelected,
    updateSelectedList,
} from "./FilterUtil";

interface Props {
    filter: StatFilter;
    selected: Selected[];
    isPublic: boolean;
    addSelected: (filterValue: string, selection: Selected) => void;
    removeSelected: (filterValue: string, optionValue: string) => void;
    setSelected: (filterValue: string, selection: Selected[]) => void;
    setMultiSelectValue: (value: string | undefined) => void;
    multiSelectValue?: string;
}

export function Filter({
    filter,
    selected,
    isPublic,
    addSelected,
    removeSelected,
    setSelected,
    multiSelectValue,
    setMultiSelectValue,
}: Props): JSX.Element {
    const [multiSelectOptions, setMultiSelectOptions] = useState<Selected[]>(
        [],
    );
    const [multiSelectSelected, setMultiSelectSelected] = useState<Selected[]>(
        [],
    );
    const { translation, translateFilter } = useTranslation();
    const inMultiSelection = multiSelectValue === filter.value;
    const disabled = Boolean(multiSelectValue) && !inMultiSelection;
    const selectedValues = inMultiSelection
        ? multiSelectSelected.map((s) => s.key)
        : selected.map((s) => s.key);

    const addMissingMultiSelectOptions = () => {
        const newMultiSelectOptions = selected.filter(
            (s) =>
                s.options.length > 1 &&
                !multiSelectOptions.some((ms) => ms.key === s.key),
        );
        if (newMultiSelectOptions.length > 0) {
            setMultiSelectOptions([
                ...multiSelectOptions,
                ...newMultiSelectOptions,
            ]);
        }
    };

    useEffect(addMissingMultiSelectOptions, [selected]);

    const isOptionDisabled = (optionValue: string): boolean => {
        if (filter.isOptionDisabled == null) {
            return false;
        }
        return filter.isOptionDisabled(selectedValues, optionValue);
    };

    const clear = () => {
        if (inMultiSelection) {
            setMultiSelectSelected([]);
        } else {
            setSelected(filter.value, getDefaultSelected(filter));
        }
    };

    const clickMultiSelection = () => {
        if (inMultiSelection) {
            if (multiSelectSelected.length > 1) {
                const selection = combineSelected(multiSelectSelected);
                if (!selected.some((s) => s.key === selection.key)) {
                    addSelected(filter.value, selection);
                }
            }
            setMultiSelectValue(undefined);
        } else {
            setMultiSelectSelected([]);
            setMultiSelectValue(filter.value);
        }
    };

    const renderOptions = () => {
        const res: MultiSelectChild[] = [];

        if (inMultiSelection) {
            for (const o of filter.options ?? []) {
                if (!isAllOption(o.value)) {
                    res.push(
                        <option key={o.value} value={o.value}>
                            {translateFilter(o.name)}
                        </option>,
                    );
                }
            }
        } else {
            for (const s of multiSelectOptions) {
                res.push(
                    <option
                        key={s.key}
                        value={s.key}
                        disabled={isOptionDisabled(s.key)}
                    >
                        {s.name}
                    </option>,
                );
            }

            for (const o of filter.options ?? []) {
                res.push(
                    <option
                        key={o.value}
                        value={o.value}
                        disabled={
                            Boolean(o.disabled) || isOptionDisabled(o.value)
                        }
                    >
                        {translateFilter(o.name)}
                    </option>,
                );
            }
        }

        return res;
    };

    const placeholder = translation.filters.selectOption;

    return (
        <>
            <Label>{translateFilter(filter.name)}</Label>

            <FilterButtons
                filter={filter}
                isPublic={isPublic}
                inMultiSelection={inMultiSelection}
                disabled={disabled}
                clickMultiSelection={clickMultiSelection}
                clear={clear}
            />
            <MultiSelect
                values={selectedValues}
                disabled={disabled}
                onChange={(value, checked) => {
                    if (inMultiSelection) {
                        const option = findOption(filter, value);
                        if (option != null) {
                            setMultiSelectSelected(
                                updateSelectedList(
                                    multiSelectSelected,
                                    option,
                                    checked,
                                ),
                            );
                        }
                    } else {
                        if (checked) {
                            const option = findOption(filter, value);
                            if (option) {
                                addSelected(filter.value, makeSelected(option));
                            } else {
                                const selection = multiSelectOptions.find(
                                    (s) => s.key === value,
                                );
                                if (selection != null) {
                                    addSelected(filter.value, selection);
                                }
                            }
                        } else {
                            removeSelected(filter.value, value);
                        }
                    }
                }}
                placeholder={placeholder}
                aria-label={placeholder}
            >
                {renderOptions()}
            </MultiSelect>
        </>
    );
}

function getDefaultSelected(
    filter: StatFilter,
    selectedOptionValues?: string[],
): Selected[] {
    const result: Selected[] = [];

    if (selectedOptionValues != null) {
        for (const value of selectedOptionValues) {
            const selections = value
                .split(",")
                .map((value) => findOption(filter, value))
                .filter((o): o is FilterOption => o != null)
                .map((o) => makeSelected(o));

            if (selections.length === 1) {
                result.push(selections[0]);
            } else if (selections.length > 1) {
                result.push(combineSelected(selections));
            }
        }
    }

    if (result.length > 0) {
        return result;
    }

    return (
        filter.options?.filter((o) => o.selected).map((o) => makeSelected(o)) ??
        []
    );
}

Filter.getDefaultSelected = getDefaultSelected;

function findOption(
    filter: StatFilter,
    value: string,
): FilterOption | undefined {
    return filter.options?.find((o) => o.value === value);
}
